"use client";

import { useState, useEffect } from "react";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { getStripe } from "@/lib/stripe";
import { Loader2, CheckCircle2, AlertCircle, CreditCard } from "lucide-react";
import { fsElement } from "@/lib/fs";

type PaymentFormProps = {
  clientSecret: string;
  amountCents: number;
  onSuccess: () => void;
  onCancel?: () => void;
  /** When false, hide the Skip button (e.g. on pay-later page where user came to pay) */
  showSkip?: boolean;
};

function CheckoutForm({
  amountCents,
  onSuccess,
  onCancel,
  showSkip = true,
}: Omit<PaymentFormProps, "clientSecret">) {
  const stripe = useStripe();
  const elements = useElements();
  const [elementReady, setElementReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "processing" | "succeeded" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // If the payment form never becomes ready (e.g. missing Stripe key, bad client secret), show a message
  useEffect(() => {
    if (elementReady || loadError) return;
    const t = setTimeout(() => {
      setLoadError((prev) => (prev ? prev : "Payment form is taking longer than usual to load."));
    }, 8000);
    return () => clearTimeout(t);
  }, [elementReady, loadError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setStatus("processing");
    setErrorMessage(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: "if_required",
    });

    if (error) {
      setStatus("error");
      setErrorMessage(error.message || "Payment failed. Please try again.");
    } else {
      setStatus("succeeded");
      onSuccess();
    }
  };

  if (status === "succeeded") {
    return (
      <div className="text-center py-6">
        <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
        <h3 className="font-semibold text-foreground">Payment Successful</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Your booking has been confirmed and payment of £{(amountCents / 100).toFixed(2)} received.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-foreground">Payment</h3>
        <span className="ml-auto text-lg font-bold text-foreground">
          £{(amountCents / 100).toFixed(2)}
        </span>
      </div>

      <div className="min-h-[220px]" aria-busy={!elementReady} aria-live="polite">
        {!elementReady && !loadError && (
          <p className="text-sm text-muted-foreground mb-3">Loading payment form…</p>
        )}
        <PaymentElement
          options={{
            layout: "tabs",
          }}
          onReady={() => {
            setElementReady(true);
            setLoadError(null);
          }}
          onLoadError={(event) => {
            setLoadError(event?.error?.message ?? "Payment form could not be loaded.");
          }}
        />
      </div>

      {loadError && (
        <div className="flex items-start gap-2 rounded-md bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-200">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p>{loadError} Try refreshing the page or check that {process.env.NODE_ENV === "development" ? "NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY is set in .env.local" : "your connection is stable"}.</p>
        </div>
      )}

      {errorMessage && (
        <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p>{errorMessage}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!stripe || !elements || !elementReady || !!loadError || status === "processing"}
          className="flex-1 rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95 transition-opacity"
          {...fsElement("Stripe Pay Button", { id: "stripe-pay", section: "payment" })}
        >
          {status === "processing" ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing...
            </span>
          ) : (
            `Pay £${(amountCents / 100).toFixed(2)}`
          )}
        </button>
        {showSkip && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={status === "processing"}
            className="rounded-lg border border-border px-4 py-3 text-sm font-medium text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
            {...fsElement("Payment Skip Button", { id: "stripe-pay-skip", section: "payment" })}
          >
            Skip
          </button>
        )}
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Secure payment powered by Stripe. Your card details are never stored on our servers.
      </p>
    </form>
  );
}

export function PaymentForm({
  clientSecret,
  amountCents,
  onSuccess,
  onCancel,
  showSkip = true,
}: PaymentFormProps) {
  const stripePromise = getStripe();

  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-lg">
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: "#6366f1",
              borderRadius: "8px",
            },
          },
        }}
      >
        <CheckoutForm
          amountCents={amountCents}
          onSuccess={onSuccess}
          onCancel={onCancel}
          showSkip={showSkip}
        />
      </Elements>
    </div>
  );
}
